/**
 * @author       Matthieu Devlin (matt@zumper.com)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  optimizely.ts
 */

import { ConsoleLogHandler, setLogHandler } from '@optimizely/js-sdk-logging'
import { Config, type UserAttributes } from '@optimizely/optimizely-sdk'
import {
  ReactSDKClient,
  createInstance as optimizelyCreateInstance,
} from '@optimizely/react-sdk'

// Initializing the optimizely instance to get the constructor will raise an error because we supply no datafile or
// sdkKey - we set the LogHandler to null to suppress this error and set it to a new ConsoleLogHandler after getting
// the constructor
setLogHandler(null)

const OptimizelyReactSDKClient = optimizelyCreateInstance({})
  .constructor as new (
  config: Config
) => ReactSDKClient

setLogHandler(new ConsoleLogHandler())

export class OptimizelyClient extends OptimizelyReactSDKClient {
  public readonly activatedExperiments: Set<string>
  private readonly variationMap: ReadonlyMap<string, string>

  constructor(
    optimizelyConfig: Config,
    variationMap: ReadonlyMap<string, string>,
    activatedExperiments: Set<string>
  ) {
    super(optimizelyConfig)
    this.activatedExperiments = activatedExperiments
    this.variationMap = variationMap
  }

  activate(
    experimentKey: string,
    overrideUserId?: string | undefined,
    overrideAttributes?: UserAttributes | undefined
  ) {
    this.activatedExperiments.add(experimentKey)

    return super.activate(experimentKey, overrideUserId, overrideAttributes)
  }

  exportVariationMap(): Record<string, string> {
    return Object.fromEntries(this.variationMap)
  }
}

export const createInstance = (
  optimizelyConfig: Config,
  variationMap: Map<string, string>,
  activatedExperiments: Set<string>
): OptimizelyClient => {
  return new OptimizelyClient(
    optimizelyConfig,
    variationMap,
    activatedExperiments
  )
}
